/*
 * Garantía Extendida: recuadro de la ficha y cantidad de garantías en el carrito.
 *
 * Cada garantía se agrega como una línea aparte, igual que la instalación, con la propiedad
 * oculta `_garantia_de` (ID de la variante del aire que cubre). Con ella se mantiene la regla
 * de negocio "una garantía por unidad": si el cliente cambia la cantidad del aire, las
 * garantías se ajustan; si lo quita, se quitan. Si el cliente quita la garantía, no se vuelve
 * a agregar.
 *
 * Se carga en todas las páginas desde layout/theme.liquid porque el ajuste también tiene que
 * correr en el carrito.
 */
(() => {
  const PROPIEDAD_VINCULO = '_garantia_de';
  const SECCION_BURBUJA = 'cart-icon-bubble';

  const pedirJson = (url, cuerpo) =>
    fetch(url, {
      method: cuerpo ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: cuerpo ? JSON.stringify(cuerpo) : undefined,
    }).then((respuesta) => {
      if (!respuesta.ok) throw new Error(`Garantía Extendida: ${url} respondió ${respuesta.status}`);
      return respuesta.json();
    });

  const actualizarBurbuja = (secciones) => {
    const html = secciones && secciones[SECCION_BURBUJA];
    const burbuja = document.getElementById(SECCION_BURBUJA);
    if (!html || !burbuja) return;
    const nueva = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (nueva) burbuja.innerHTML = nueva.innerHTML;
  };

  /*
   * Deja la cantidad de garantías de cada aire igual a la cantidad del aire.
   * Si hay más de una línea de garantía para el mismo aire (por ejemplo 1 año y 2 años),
   * se respetan y solo se ajusta la última. Devuelve true si cambió algo.
   */
  const sincronizarCantidades = async () => {
    const carrito = await pedirJson(`${window.routes.cart_url}.js`);

    const unidadesPorVariante = {};
    const garantiasPorVariante = {};
    carrito.items.forEach((linea) => {
      const cubierta = linea.properties && linea.properties[PROPIEDAD_VINCULO];
      if (cubierta) {
        (garantiasPorVariante[cubierta] = garantiasPorVariante[cubierta] || []).push(linea);
      } else {
        unidadesPorVariante[linea.variant_id] = (unidadesPorVariante[linea.variant_id] || 0) + linea.quantity;
      }
    });

    const cambios = {};
    Object.entries(garantiasPorVariante).forEach(([variante, lineas]) => {
      const unidades = unidadesPorVariante[variante] || 0;
      let restantes = unidades;
      lineas.forEach((linea, indice) => {
        const ultima = indice === lineas.length - 1;
        const deseada = ultima ? restantes : Math.min(linea.quantity, restantes);
        restantes -= deseada;
        if (deseada !== linea.quantity) cambios[linea.key] = deseada;
      });
    });

    if (Object.keys(cambios).length === 0) return false;

    const respuesta = await pedirJson(`${window.routes.cart_update_url}.js`, {
      updates: cambios,
      sections: SECCION_BURBUJA,
    });
    actualizarBurbuja(respuesta.sections);
    return true;
  };

  const enCarrito = () => document.querySelector('cart-items') !== null;

  /* Recarga el carrito para mostrar el ajuste, como máximo una vez cada 10 s (evita ciclos). */
  const recargarCarrito = () => {
    try {
      const ultima = Number(sessionStorage.getItem('rd-garantia-recarga') || 0);
      if (Date.now() - ultima < 10000) return;
      sessionStorage.setItem('rd-garantia-recarga', String(Date.now()));
    } catch (error) {
      return;
    }
    window.location.reload();
  };

  if (!customElements.get('rd-garantia-extendida')) {
    customElements.define(
      'rd-garantia-extendida',
      class RdGarantiaExtendida extends HTMLElement {
        connectedCallback() {
          this.tramos = JSON.parse(this.querySelector('[data-rd-ga-tramos]').textContent);
          this.precios = JSON.parse(this.querySelector('[data-rd-ga-precios]').textContent);
          this.opciones = [...this.querySelectorAll('input[data-rd-ga-anios]')];
          this.mostrar(this.dataset.varianteId);

          //INFO: Casillas que se excluyen entre sí: se puede elegir un plazo o ninguno.
          this.opciones.forEach((opcion) =>
            opcion.addEventListener('change', () => {
              if (opcion.checked) this.opciones.forEach((otra) => otra !== opcion && (otra.checked = false));
            })
          );

          this.cancelarCambioVariante = subscribe(PUB_SUB_EVENTS.variantChange, (evento) => {
            const datos = evento && evento.data;
            if (!datos || datos.sectionId !== this.dataset.sectionId || !datos.variant) return;
            this.mostrar(String(datos.variant.id));
          });
        }

        disconnectedCallback() {
          if (this.cancelarCambioVariante) this.cancelarCambioVariante();
        }

        mostrar(varianteId) {
          this.varianteId = varianteId;
          const precio = this.precios[varianteId];
          const tramo = this.tramos.find((t) => precio >= t.min && precio <= t.max);

          this.hidden = !tramo;
          if (!tramo) {
            this.opciones.forEach((opcion) => (opcion.checked = false));
            return;
          }

          this.opciones.forEach((opcion) => {
            const plan = tramo.planes.find((p) => String(p.anios) === opcion.dataset.rdGaAnios);
            opcion.value = plan.id;
            opcion.closest('label').querySelector('[data-rd-ga-precio]').textContent = plan.precio;
          });
        }

        /* Agrega la garantía elegida para la cantidad que se acaba de agregar del aire. */
        async agregarGarantia() {
          const elegida = this.opciones.find((opcion) => opcion.checked);
          if (this.hidden || !elegida || !elegida.value) return;

          const formulario = document.getElementById(this.dataset.formId);
          const cantidad = parseInt(formulario && new FormData(formulario).get('quantity'), 10) || 1;

          const propiedades = {
            [PROPIEDAD_VINCULO]: this.varianteId,
            'Producto cubierto': this.dataset.productoTitulo,
          };
          if (this.querySelector('[data-rd-ga-comercial]').checked) propiedades['Uso comercial'] = 'Sí';

          const respuesta = await pedirJson(`${window.routes.cart_add_url}.js`, {
            items: [{ id: Number(elegida.value), quantity: cantidad, properties: propiedades }],
            sections: SECCION_BURBUJA,
          });
          actualizarBurbuja(respuesta.sections);
        }
      }
    );
  }

  const iniciar = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, async (evento) => {
      if (!evento || evento.source === 'rd-garantia') return;
      try {
        if (evento.source === 'product-form') {
          const recuadro = [...document.querySelectorAll('rd-garantia-extendida')].find(
            (r) => r.varianteId === String(evento.productVariantId)
          );
          if (recuadro) await recuadro.agregarGarantia();
        }
        const cambio = await sincronizarCantidades();
        if (cambio && enCarrito()) recargarCarrito();
      } catch (error) {
        console.error(error);
      }
    });

    if (enCarrito()) {
      sincronizarCantidades()
        .then((cambio) => {
          if (cambio) recargarCarrito();
        })
        .catch((error) => console.error(error));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})();
